/**
 * SEOHead - Dynamic SEO meta tags and JSON-LD structured data
 * 
 * Renders:
 * - Dynamic title based on current dial state
 * - Meta description with location and celestial context
 * - Open Graph tags for social sharing
 * - JSON-LD structured data for search engines
 * 
 * © Optimal Mastery LLC and the Onesce family of applied wellness brands.
 */

import { useEffect, useCallback } from 'react';
import { SEO_GEO_CONFIG } from '../utils/styleCard';
import { GLOSSARY, FAQ } from '../utils/glossaryFAQ';

/**
 * Hook to manage SEO meta tags based on dial state
 */
export const useSEO = (dialState = {}) => {
  const {
    location = {},
    date = null,
    cosmicData = {},
    selectedPlanet = null,
    comparisonMode = false
  } = dialState;

  // Generate dynamic title
  const generateTitle = useCallback(() => {
    const { titleTemplate, pageTitles } = SEO_GEO_CONFIG.dynamicMeta;
    
    let pageTitle = pageTitles.home;
    
    if (location.city) {
      pageTitle = pageTitles.dial.replace('{location}', location.city.split(',')[0]);
    }
    
    if (selectedPlanet) {
      pageTitle = `${selectedPlanet} in ${cosmicData[selectedPlanet]?.zodiacSign || 'Transit'} - ${pageTitle}`;
    }
    
    if (comparisonMode) {
      pageTitle = `Comparison Analysis - ${pageTitle}`;
    }
    
    return titleTemplate.replace('{pageTitle}', pageTitle);
  }, [location, selectedPlanet, cosmicData, comparisonMode]);

  // Generate dynamic description
  const generateDescription = useCallback(() => {
    const parts = [];
    
    // Location context
    if (location.city) {
      parts.push(`Celestial positions for ${location.city}`);
    } else {
      parts.push('Real-time celestial orientation instrument');
    }
    
    // Time context
    if (date) {
      const d = new Date(date);
      parts.push(`on ${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`);
    } else {
      parts.push('(live)');
    }
    
    // Add cosmic highlight
    if (cosmicData.sunSign) {
      parts.push(`Sun in ${cosmicData.sunSign}`);
    }
    if (cosmicData.moonPhase) {
      parts.push(`${cosmicData.moonPhase} Moon`);
    }
    
    // Brand tagline
    parts.push('- ' + SEO_GEO_CONFIG.brandNarrative.tagline);
    
    return parts.join(' ');
  }, [location, date, cosmicData]);

  // Generate JSON-LD structured data
  const generateStructuredData = useCallback(() => {
    const { structuredData } = SEO_GEO_CONFIG;
    
    // Build FAQ schema from actual FAQs
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.slice(0, 10).map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    };
    
    // Build WebApplication schema with geo context
    const appSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'THE ONESCENT DIAL',
      applicationCategory: 'LifestyleApplication',
      operatingSystem: 'Any',
      browserRequirements: 'Requires JavaScript',
      description: SEO_GEO_CONFIG.brandNarrative.mission,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      author: {
        '@type': 'Organization',
        name: 'Optimal Mastery LLC',
        url: 'https://onesce.com'
      }
    };
    
    // Add location context if available
    if (location.latitude && location.longitude) {
      appSchema.contentLocation = {
        '@type': 'Place',
        geo: {
          '@type': 'GeoCoordinates',
          latitude: location.latitude,
          longitude: location.longitude
        },
        name: location.city || 'User Location'
      };
    }
    
    // Build DefinedTermSet from glossary
    const glossaryTerms = Object.entries(GLOSSARY).slice(0, 20).map(([term, data]) => ({
      '@type': 'DefinedTerm',
      name: term,
      description: data.definition,
      inDefinedTermSet: {
        '@type': 'DefinedTermSet',
        name: 'Cycles in Harmony Glossary'
      }
    }));
    
    const glossarySchema = {
      '@context': 'https://schema.org',
      '@type': 'DefinedTermSet',
      name: 'Cycles in Harmony (CiH) Glossary',
      description: 'Comprehensive terminology for celestial orientation and cyclical awareness',
      hasDefinedTerm: glossaryTerms
    };
    
    return [appSchema, faqSchema, glossarySchema];
  }, [location]);

  // Apply meta tags to document
  useEffect(() => {
    const title = generateTitle();
    const description = generateDescription();
    const structuredData = generateStructuredData();
    
    // Update title
    document.title = title;
    
    // Update or create meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;
    
    // Update Open Graph tags
    const ogTags = {
      'og:title': title,
      'og:description': description,
      'og:type': SEO_GEO_CONFIG.dynamicMeta.openGraph.type,
      'og:site_name': SEO_GEO_CONFIG.dynamicMeta.openGraph.siteName,
      'og:locale': SEO_GEO_CONFIG.dynamicMeta.openGraph.locale
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let ogTag = document.querySelector(`meta[property="${property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', property);
        document.head.appendChild(ogTag);
      }
      ogTag.content = content;
    });
    
    // Update Twitter Card tags
    const twitterTags = {
      'twitter:card': SEO_GEO_CONFIG.dynamicMeta.twitterCard.card,
      'twitter:title': title,
      'twitter:description': description
    };
    
    Object.entries(twitterTags).forEach(([name, content]) => {
      let twitterTag = document.querySelector(`meta[name="${name}"]`);
      if (!twitterTag) {
        twitterTag = document.createElement('meta');
        twitterTag.name = name;
        document.head.appendChild(twitterTag);
      }
      twitterTag.content = content;
    });
    
    // Add/update JSON-LD structured data
    let jsonLdScript = document.querySelector('script[data-seo="structured-data"]');
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.type = 'application/ld+json';
      jsonLdScript.setAttribute('data-seo', 'structured-data');
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.textContent = JSON.stringify(structuredData);
    
    // Add geo meta tags if location available
    if (location.latitude && location.longitude) {
      const geoTags = {
        'geo.position': `${location.latitude};${location.longitude}`,
        'geo.placename': location.city || '',
        'ICBM': `${location.latitude}, ${location.longitude}`
      };
      
      Object.entries(geoTags).forEach(([name, content]) => {
        if (!content) return;
        let geoTag = document.querySelector(`meta[name="${name}"]`);
        if (!geoTag) {
          geoTag = document.createElement('meta');
          geoTag.name = name;
          document.head.appendChild(geoTag);
        }
        geoTag.content = content;
      });
    }
    
  }, [generateTitle, generateDescription, generateStructuredData, location]);

  return {
    title: generateTitle(),
    description: generateDescription()
  };
};

/**
 * SEOHead component - Call this at App level with dial state
 */
const SEOHead = ({ dialState }) => {
  useSEO(dialState);
  return null; // No visual render, just manages document head
};

export default SEOHead;
